import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import { ThemeMode } from 'config';

// assets
import logo from 'assets/images/logo.jpg';
import TranslateTwoToneIcon from '@mui/icons-material/TranslateTwoTone';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { getPictureUploadUrl } from 'utils/helperFunction';
import useTenant from 'hooks/useTenant';
import api from 'api';
import { ListItemIcon } from '@mui/material';

// ==============================|| TENANT SECTION ||============================== //

const TenantSection = () => {
    const { mode, borderRadius, i18n, onChangeLocale } = useConfig();

    const theme = useTheme();
    const downMD = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const dispatch = useDispatch();
    const { user } = useAuth();
    const { getTenant, setTenant } = useTenant();
    const [tenant, setCurrentTenant] = useState(getTenant());
    const [tenants, setTenants] = useState([]);

    const handleListItemClick = (event, index) => {
        setCurrentTenant(index);
        setTenant(index);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    useEffect(() => {
        async function getTenants() {
            const res = await api.find('user/getTenants')
            if (res && res.items.length) {
                setTenants(res.items);   
            }
        }

        getTenants();
    }, [tenant]);

    return (
        <>
            <Box sx={{ ml: { xs: 0, sm: 2 } }}>
                <Avatar
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        }
                    }}
                        variant="square"
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        color="inherit"
                    >
                    <img 
                        src={ tenant && tenant.logoPicture && tenant.logoPicture.fileName ? 
                            getPictureUploadUrl() + 'media/' + tenant.logoPicture.fileName : logo } 
                        width={25}
                        stroke={1.5} size="1.3rem" alt='' />
                    </Avatar>
            </Box>

            <Popper
                placement={downMD ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [downMD ? 0 : 0, 20]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={downMD ? 'top-left' : 'top'} in={open} {...TransitionProps}>
                            <Paper elevation={16}>
                                {open && (
                                    <List
                                        sx={{
                                            width: '100%',
                                            minWidth: 200,
                                            maxWidth: { xs: 250, sm: 280 },
                                            borderRadius: `${borderRadius}px`
                                        }}
                                    >
                                    {
                                        tenants.map((t, i) => (
                                        <ListItemButton selected={t._id === tenant._id} onClick={(event) => handleListItemClick(event, t)}>
                                            <ListItemIcon>
                                            <img 
                                                src={ t.logoPicture && t.logoPicture.fileName ? 
                                                    getPictureUploadUrl() + 'media/' + t.logoPicture.fileName : logo } 
                                                width={25}
                                                stroke={1.5} size="1.3rem" alt='' />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">{t.name}</Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                        ))
                                    }
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default TenantSection;
